import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, from } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { switchMap } from 'rxjs/operators';

import { CookieDeviceIdReadyService } from '../services/cookie-device-id-ready.service';

/**
 * HTTP Interceptor that blocks all requests until the cookie device ID (cdid)
 * is ready or timeout is reached.
 *
 * This prevents multiple concurrent requests from each getting their own cdid
 * when the backend sets the cookie on the first response.
 *
 * The interceptor:
 * 1. Allows user fetch requests to pass through immediately (they need to get the cdid)
 * 2. Blocks all other requests until cookie device ID is ready or timeout
 * 3. Only runs in browser environment (SSR compatible)
 */
@Injectable()
export class BlockingInterceptor implements HttpInterceptor {
  constructor(
    private cookieDeviceIdReadyService: CookieDeviceIdReadyService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Only run in browser environment (SSR compatibility)
    if (!isPlatformBrowser(this.platformId)) {
      return next.handle(request);
    }

    // Check if this is the user fetch request itself - if so, let it pass
    if (this.isUserFetchRequest(request)) {
      return next.handle(request);
    }

    // If already ready or timeout reached, let the request pass
    if (
      this.cookieDeviceIdReadyService.isReadyNow() ||
      this.cookieDeviceIdReadyService.isTimeoutReached()
    ) {
      return next.handle(request);
    }

    // Wait for cookie device ID to be ready or timeout before proceeding
    return from(this.cookieDeviceIdReadyService.waitForReadyOrTimeout()).pipe(
      switchMap(() => next.handle(request)),
    );
  }

  /**
   * Check if this request is the user fetch request that should not be blocked.
   * We need to identify the /api/v2/user GET request that fetches user data.
   */
  private isUserFetchRequest(request: HttpRequest<any>): boolean {
    // Match the user fetch endpoint pattern
    // The exact path might vary, so we use a flexible pattern
    const isUserPath = request.url.includes('/api/v2/user');

    return isUserPath && ['GET', 'OPTIONS', 'HEAD'].includes(request.method);
  }
}
